import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { SAVE_POSTS, SAVE_POST_PICTURE, SAVE_POST_VIDEOS } from "../../constant/Apipath";
import { Postrequestcall } from "../../request/Postrequest";
import { Cancel, Collections } from "@mui/icons-material";
import { Fileuploadrequestcall } from "../../request/Fileuploadrequest";
import { BIBlue, imageCompress } from "../../assets/helpers";
import { English } from "../../constant/EnglishFallback";

export const AddPostDialog = (props) => {
  const { loginData, multiLanguageData } = useSelector((state) => state?.main);

  const {
    TEXT_WRITE_A_POST = English?.Home?.TEXT_WRITE_A_POST,
    MODAL_ADD_POST_PLACEHOLDER = English?.Home?.MODAL_ADD_POST_PLACEHOLDER,
    BUTTON_CANCEL = English?.Home?.BUTTON_CANCEL,
    BUTTON_POST = English?.Home?.BUTTON_POST,
    MODAL_BUTTON_POSTING = English?.Home?.MODAL_BUTTON_POSTING
  } = multiLanguageData?.Home || {};

  const [selectedImages, setSelectedImages] = useState([]);
  const [postInputText, setPostInputText] = useState("");
  const [isPostSaving, setIsPostSaving] = useState(false);
  const fileInputRef = useRef(null);
  const nextIdRef = useRef(0); // Add this to generate unique IDs

  const { open, onClose, updatePosts, appRecordId, appId, messageTitle } = props;

  const { UserId, accessToken, pictureUrl, FirstName, LastName, TradeName } = useMemo(() => {
    return {
      UserId: loginData?.userInformation?.UserId,
      accessToken: loginData?.accessToken,
      pictureUrl: loginData?.pictureUrl,
      FirstName: loginData?.userInformation?.FirstName,
      LastName: loginData?.userInformation?.LastName,
      TradeName: loginData?.userInformation?.TradeName
    };
  }, [loginData]);

  const handleAddPhotosClick = () => {
    fileInputRef.current.click();
  };

  const handleImageCompression = async (file) => {
    try {
      return await imageCompress(file);
    } catch (error) {
      console.error("handleImageCompression-> Error When Handling Image COmpression: ", error);
      return file;
    }
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);

    // Create preview with unique IDs
    const newFiles = files.map((file) => ({
      id: nextIdRef.current++, // Add unique ID
      preview: URL.createObjectURL(file),
      raw: file,
      type: file?.type
    }));

    setSelectedImages((prev) => [...prev, ...newFiles]);

    // Compress files asynchronously
    newFiles.forEach(async (file) => {
      if (file?.type.includes("image")) {
        const compressedFile = await handleImageCompression(file.raw);
        setSelectedImages((prev) =>
          prev.map((item) => (item.id === file.id ? { ...item, raw: compressedFile } : item))
        );
      }
    });

    event.target.value = "";
  };

  const handleRemoveImage = (targetId) => {
    const imageToRemove = selectedImages.find((img) => img.id === targetId);
    if (imageToRemove?.preview) {
      URL.revokeObjectURL(imageToRemove.preview);
    }
    const updatedFiles = selectedImages.filter((item) => item.id !== targetId);
    setSelectedImages(updatedFiles);
  };

  const handlePostInputText = (event) => {
    let inputValue = event.target.value;
    // If the first character is alphanumeric or emoji, allow any input
    if (/^[A-Za-z0-9\u{1F300}-\u{1FAFF}]/u.test(inputValue)) {
      // Do nothing, allow all characters
    } else {
      // Remove leading non-alphanumeric characters (except emojis)
      inputValue = inputValue.replace(/^[^A-Za-z0-9\u{1F300}-\u{1FAFF}]+/u, "");
      // Remove special characters except emojis and spaces in the middle
      inputValue = inputValue.replace(/[^A-Za-z0-9\u{1F300}-\u{1FAFF}\s]/gu, "");
    }
    if (inputValue.trim() === "" && inputValue !== "") {
      return;
    }
    setPostInputText(inputValue);
  };

  const clearState = () => {
    selectedImages.forEach((img) => {
      if (img?.preview) URL.revokeObjectURL(img.preview);
    });
    setSelectedImages([]);
    setPostInputText("");
    onClose();
    updatePosts();
  };

  const handleSavePost = async () => {
    const imageList = selectedImages.filter((image) => image.type.includes("image"));
    const videoList = selectedImages.filter((video) => video.type.includes("video"));
    setIsPostSaving(true);

    try {
      const createPostObj = (message) => ({
        UserId: UserId,
        ReturnRecordId: true,
        ReturnRecordError: false,
        SaveList: [
          {
            Id: 0,
            FormatTypeId: 1,
            PostedByUserId: UserId,
            AppId: appId,
            AppRecordId: appRecordId,
            Message: message.trim() || "<<<picture>>>",
            ModifiedBy: UserId
          }
        ]
      });

      let postId = null;

      if (videoList.length === 0 && imageList.length === 0) {
        const obj = createPostObj(postInputText);
        await Postrequestcall(SAVE_POSTS, obj, accessToken);
      }

      if (imageList.length > 0) {
        const obj = createPostObj(postInputText);
        const postResult = await Postrequestcall(SAVE_POSTS, obj, accessToken);
        postId = postResult.data?.data[0]?.objData?.id;
      }

      await Promise.all([
        postId && imageList.length > 0 ? savePostPictures(postId, 0, imageList) : null,
        videoList.length > 0 ? savePostVideo(postInputText, videoList) : null
      ]);

      clearState();
      setIsPostSaving(false);
    } catch (error) {
      setIsPostSaving(false);
      console.log("handleSavePost-> Error When Saving Post: ", error);
    }
  };

  const savePostPictures = async (prId, id, imageList) => {
    try {
      for (let i = 0; i < imageList?.length; i++) {
        const formData = new FormData();
        formData.append("Id", 0);
        formData.append("PostId", prId);
        formData.append("ModifiedBy", UserId);
        formData.append("Pictures", imageList[i]?.raw);
        await Fileuploadrequestcall(SAVE_POST_PICTURE, formData, accessToken);
      }
    } catch (error) {
      setIsPostSaving(false);
      console.log("savePostPictures-> Error When Saving Post Picture: ", error);
    }
  };

  const savePostVideo = async (postInputText, videoList) => {
    try {


      // console.log("savePostVideo->", videoList);
      //return;

      await Promise.all(
        videoList?.map(async (video) => {
          const obj = {
            UserId: UserId,
            ReturnRecordId: true,
            ReturnRecordError: false,
            SaveList: [
              {
                Id: 0,
                FormatTypeId: 7,
                PostedByUserId: UserId,
                AppId: appId,
                AppRecordId: appRecordId,
                Message: postInputText?.trim() !== "" ? postInputText : "<<<video>>>",
                ModifiedBy: UserId
              }
            ]
          };

          const postResult = await Postrequestcall(SAVE_POSTS, obj, accessToken);
          const postResId = postResult.data?.data[0]?.objData?.id ?? 0;

          const formData = new FormData();
          formData.append("Id", 0);
          formData.append("PostId", postResId);
          formData.append("FileName", video?.raw);
          formData.append("Description", postInputText);
          formData.append("ModifiedBy", UserId);
          formData.append("DurationInSeconds", 100);
          await Fileuploadrequestcall(SAVE_POST_VIDEOS, formData, accessToken);
        })
      );
    } catch (error) {
      console.log("savePostVideo-> Error When Saving Post Video: ", error);
    }
  };

  // Revoke blob URLs on unmount
  useEffect(() => {
    return () => {
      selectedImages.forEach((img) => {
        if (img?.preview) URL.revokeObjectURL(img.preview);
      });
    };
  }, []);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onClose();
        }
      }}
      maxWidth="sm"
      fullWidth>
      <DialogTitle>{TEXT_WRITE_A_POST}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" className="new-post-input-box">
            {messageTitle}
          </Typography>

          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar alt="User" src={pictureUrl} />
            <Stack>
              <Typography variant="body1">{`${FirstName} ${LastName}`}</Typography>
              <Typography variant="body2" color="text.secondary">
                {TradeName}
              </Typography>
            </Stack>
          </Stack>

          <Divider />

          <TextField
            slotProps={{
              input: {
                multiline: true,
                inputComponent: "textarea"
              }
            }}
            rows={3}
            placeholder={MODAL_ADD_POST_PLACEHOLDER}
            variant="outlined"
            value={postInputText}
            onChange={handlePostInputText}
            fullWidth
          />

          {selectedImages.length > 0 && (
            <>
              {/* Image Previews */}
              <Box
                sx={{
                  display: "flex",
                  overflowX: "auto",
                  gap: 2,
                  padding: 1,
                  whiteSpace: "nowrap",
                  maxHeight: 150,
                  width: "100%"
                }}>
                {selectedImages.map(
                  (image) =>
                    image.type.includes("image") && (
                      <Box
                        key={image.id} // Use unique ID instead of index
                        sx={{
                          position: "relative",
                          width: 100,
                          height: 100,
                          backgroundImage: `url(${image.preview})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          borderRadius: 2,
                          flexShrink: 0
                        }}>
                        <IconButton
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            color: "white",
                            backgroundColor: "rgba(0,0,0,0.5)"
                          }}
                          onClick={() => handleRemoveImage(image.id)}>
                          <Cancel fontSize="small" />
                        </IconButton>
                      </Box>
                    )
                )}
              </Box>

              {/* Video Previews */}
              {selectedImages.some((img) => img.type.includes("video")) && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
                  {selectedImages.map((image) =>
                    image.type.includes("video") ? (
                      <Box
                        key={image.id}
                        sx={{
                          position: "relative",
                          width: "100%",
                          borderRadius: 2,
                          backgroundColor: "#000",
                          overflow: "hidden"
                        }}>
                        <video
                          src={image.preview}
                          controls
                          style={{
                            width: "100%",
                            maxHeight: 340,
                            display: "block",
                            objectFit: "contain"
                          }}
                        />
                        <IconButton
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            color: "white",
                            backgroundColor: "rgba(0,0,0,0.5)",
                            "&:hover": { backgroundColor: "rgba(0,0,0,0.75)" }
                          }}
                          onClick={() => handleRemoveImage(image.id)}>
                          <Cancel fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : null
                  )}
                </Box>
              )}
            </>
          )}

          <IconButton
            onClick={handleAddPhotosClick}
            style={{ cursor: "pointer", alignSelf: "flex-start", color: BIBlue }}>
            <Collections />
          </IconButton>

          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
            accept="image/*,video/*"
            multiple
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPostSaving} color="primary">
          {BUTTON_CANCEL}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSavePost}
          disabled={isPostSaving || (selectedImages.length === 0 && postInputText === "")}
          startIcon={isPostSaving ? <CircularProgress size={20} color="inherit" /> : null}>
          {isPostSaving ? MODAL_BUTTON_POSTING : BUTTON_POST}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
